// Hideo - script editor: Markdown ↔ HTML (the "Markdown" view, export, Markdown files dropped or picked, multi-line
// plain text pasted, the in-app guide) and the Markdown shortcuts typed in the visual view. Ported from the legacy
// editor.js with the same results (golden tests: tests/editor-markdown.test.ts against tests/fixtures/legacy/editor.js).
// Sub-lists are indented by two spaces per level (since 23/10/2026; before, they came out flat). Known limit kept on
// purpose for now (docs/backlog.md): alignment and line spacing are lost (htmlToMd keeps only the inline style of spans).
import { esc } from '../../utils/html'
import { MENTION_RE, chipHtml, validRef } from '../mentions'
import { noZw, stripLowc } from './html'

/** A line that starts a block of HTML written in the Markdown, and one that starts a Markdown block (heading, quote,
 *  code, divider, list): a paragraph ends before either */
const HTML_BLOCK = /^<(div|details|table|p|h\d|ul|ol|pre|blockquote|hr)/i
const MD_BLOCK = /^(#{1,4}\s|>|```|---+\s*$|\s*([-*]|\d+\.)\s)/

/** A mention's chip in the editor (chipHtml of src/features/mentions.ts, not clickable) */
type ChipFn = (type: string, id: string) => string
const editorChip: ChipFn = (type, id) => chipHtml(type, id, false)

/** Markdown → HTML: headings # to ####, paragraphs (lines joined with <br>), > quotes, - * 1. lists, - [ ] / - [x]
 *  checklists (indented items: a sub-list, written inside the list as the editor's Tab does), ``` code, --- dividers, **bold**, *italic*, `code`, ~~strikethrough~~, [text](link), @[type:id]
 *  mentions (chips); lines starting with a block tag (<div, <details, <table…) are kept as HTML. The result is cleaned
 *  with cleanHtml by the callers. */
export function mdToHtml(md: string, chip: ChipFn = editorChip): string {
  const inl = (str: string) => str.replace(MENTION_RE, (_m, ty, id) => chip(ty, id)).replace(/`([^`]+)`/g, '<code>$1</code>').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>').replace(/~~([^~]+)~~/g, '<s>$1</s>').replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2" target="_blank">$1</a>')
  const L = md.replace(/\r/g, '').split('\n')
  let o = '', i = 0, m: RegExpMatchArray | null
  while (i < L.length) {
    const l = L[i]
    if (/^```/.test(l)) { const c: string[] = []; i++; while (i < L.length && !/^```/.test(L[i])) c.push(L[i++]); i++; o += '<pre><code>' + esc(c.join('\n')) + '</code></pre>'; continue }
    if ((m = l.match(/^(#{1,4})\s+(.*)/))) { o += `<h${m[1].length}>${inl(m[2])}</h${m[1].length}>`; i++; continue }
    if (/^---+\s*$/.test(l)) { o += '<hr>'; i++; continue }
    if (/^>/.test(l)) { const c: string[] = []; while (i < L.length && /^>/.test(L[i])) c.push(L[i++].replace(/^>\s?/, '')); o += '<blockquote>' + inl(c.join('<br>')) + '</blockquote>'; continue }
    if (/^\s*([-*]|\d+\.)\s/.test(l)) {
      const items: { d: number; ord: boolean; cb: RegExpMatchArray | null; text: string }[] = []
      while (i < L.length && (m = L[i].match(/^(\s*)(\d+\.|[-*])\s(.*)/))) {
        items.push({ d: m[1].replace(/\t/g, '  ').length, ord: /\d/.test(m[2]), cb: m[3].match(/^\[( |x)\]\s(.*)/), text: m[3] })
        i++
      }
      /** The list at depth d from item j: its kind from its first item; deeper items make a list inside it */
      const list = (j: number, d: number): [string, number] => {
        const tag = items[j].ord ? 'ol' : 'ul'
        let todo = false, h = ''
        while (j < items.length && items[j].d >= d) {
          if (items[j].d > d) { const [sub, next] = list(j, items[j].d); h += sub; j = next; continue }
          const it = items[j++]
          if (it.cb) { todo = true; h += `<li${it.cb[1] === 'x' ? ' data-done="1"' : ''}>${inl(it.cb[2])}</li>` } else h += `<li>${inl(it.text)}</li>`
        }
        return [`<${tag}${todo ? ' class="todo"' : ''}>${h}</${tag}>`, j]
      }
      o += list(0, Math.min(...items.map(x => x.d)))[0]
      continue
    }
    if (HTML_BLOCK.test(l)) { const c: string[] = []; while (i < L.length && L[i].trim()) c.push(L[i++]); o += c.join('\n'); continue }
    if (!l.trim()) { i++; continue }
    const c: string[] = []
    while (i < L.length && L[i].trim() && !(MD_BLOCK.test(L[i]) || HTML_BLOCK.test(L[i]))) c.push(L[i++])
    if (!c.length) c.push(L[i++])
    o += '<p>' + inl(c.join('<br>')) + '</p>'
  }
  return o
}

/** The editor's page → Markdown (the opposite of mdToHtml): coloured spans keep their style, commented text its
 *  span.cmt, callouts and toggles (div with a class, details) stay HTML on one line. A list inside a list (in the list
 *  or in an item) is indented by two spaces. */
export function htmlToMd(root: ParentNode): string {
  const BL = /^(h[1-4]|p|div|ul|ol|blockquote|hr|pre|details)$/
  const inline = (n: Node): string => {
    if (n.nodeType === 3) return noZw(n.textContent)
    if (n.nodeType !== 1) return ''
    const el = n as HTMLElement, tag = el.tagName.toLowerCase(), c = [...el.childNodes].map(inline).join('')
    if (tag === 'strong' || tag === 'b') return c.trim() ? `**${c}**` : c
    if (tag === 'em' || tag === 'i') return c.trim() ? `*${c}*` : c
    if (tag === 's' || tag === 'strike') return `~~${c}~~`
    if (tag === 'u') return `<u>${c}</u>`
    if (tag === 'a') return `[${c}](${el.getAttribute('href') || ''})`
    if (tag === 'code') return '`' + c + '`'
    if (tag === 'br') return '\n'
    if (tag === 'input') return ''
    if (el.dataset && validRef(el.dataset.ref)) return `@[${el.dataset.ref}]`   // mention -> reference in the Markdown
    if (tag === 'span' && el.dataset.cmt) return `<span class="cmt" data-cmt="${esc(el.dataset.cmt)}">${c}</span>`   // commented text: stays linked to its comment
    if (tag === 'span' || tag === 'font') return el.getAttribute('style') ? `<span style="${el.getAttribute('style')}">${c}</span>` : c
    return c
  }
  const walk = (p: ParentNode): string => {
    let out = '', buf = ''
    const flush = () => { if (buf.trim()) out += buf.trim() + '\n\n'; buf = '' }
    ;[...p.childNodes].forEach(n => { if (n.nodeType === 1 && BL.test((n as Element).tagName.toLowerCase())) { flush(); out += block(n as HTMLElement) } else buf += inline(n) })
    flush()
    return out
  }
  const isList = (n: Element) => /^(UL|OL)$/.test(n.tagName)
  const list = (n: HTMLElement, pad: string): string[] => {
    const ol = n.tagName === 'OL', out: string[] = []
    let k = 0
    for (const li of [...n.children] as HTMLElement[]) {
      if (isList(li)) { out.push(...list(li, pad + '  ')); continue }   // a list inside the list (the editor's Tab)
      const subs = [...li.children].filter(isList) as HTMLElement[], own = subs.length ? li.cloneNode(true) as HTMLElement : li
      if (subs.length) [...own.children].filter(isList).forEach(x => x.remove())
      const cb = li.querySelector<HTMLInputElement>(':scope > input[type=checkbox]'), todo = n.classList.contains('todo') || cb, done = li.hasAttribute('data-done') || (cb && cb.checked)
      out.push(pad + (ol ? (++k) + '. ' : '- ') + (todo ? (done ? '[x] ' : '[ ] ') : '') + inline(own).trim().replace(/\n/g, ' '))
      for (const x of subs) out.push(...list(x, pad + '  '))
    }
    return out
  }
  const block = (n: HTMLElement): string => {
    const tag = n.tagName.toLowerCase()
    if (/^h[1-4]$/.test(tag)) return '#'.repeat(+tag[1]) + ' ' + inline(n).trim() + '\n\n'
    if (tag === 'ul' || tag === 'ol') return list(n, '').join('\n') + '\n\n'
    if (tag === 'blockquote') return inline(n).trim().split('\n').map(x => '> ' + x).join('\n') + '\n\n'
    if (tag === 'hr') return '---\n\n'
    if (tag === 'pre') return '```\n' + (n.textContent as string).replace(/\n$/, '') + '\n```\n\n'
    if (tag === 'details' || (tag === 'div' && n.className)) return stripLowc(n.outerHTML).replace(/\n/g, ' ') + '\n\n'
    return walk(n)
  }
  return (walk(root).trim() + '\n')
}

/** Markdown typed at the start of an empty paragraph, then a space → block (mdShortcut, src/features/editor/surface/typing.ts) */
export const MD_SHORT: Readonly<Record<string, string>> = { '#': 'h1', '##': 'h2', '###': 'h3', '####': 'h4', '-': 'ul', '*': 'ul', '+': 'ul', '1.': 'ol', '[]': 'todo', '[ ]': 'todo', '>': 'blockquote', '```': 'pre' }
/** Inline Markdown closed just before the cursor → [pattern, tag, marker length] (mdInline, src/features/editor/surface/typing.ts) */
export const MD_INLINE: readonly (readonly [RegExp, string, number])[] = [[/\*\*([^*\s](?:[^*]*[^*\s])?)\*\*$/, 'strong', 2], [/~~([^~\s](?:[^~]*[^~\s])?)~~$/, 's', 2], [/`([^`]+)`$/, 'code', 1], [/(?:^|[^*\w])\*([^*\s](?:[^*]*[^*\s])?)\*$/, 'em', 1], [/(?:^|[^_\w])_([^_\s](?:[^_]*[^_\s])?)_$/, 'em', 1]]
